import React, { useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useNavigate, Link } from 'react-router-dom';
import { GET_PROJECTS, GET_RESOURCES } from '../graphql/queries';
import { Project, Resource } from '../types';
import {
  LayoutGrid,
  Users,
  AlertTriangle,
  TrendingUp,
  Clock,
  Briefcase,
  UserCheck,
  CheckCircle,
  Activity,
  Layers,
  ChevronRight,
  Sparkles
} from 'lucide-react';

function Dashboard() {
  const navigate = useNavigate();

  // Queries
  const { data: projectsData, loading: loadingProjects } = useQuery<{ projects: Project[] }>(GET_PROJECTS);
  const { data: resourcesData, loading: loadingResources } = useQuery(GET_RESOURCES, {
    variables: { limit: 200 }
  });

  const loading = loadingProjects || loadingResources;

  // Recalculate Dashboard KPIs
  const stats = useMemo(() => {
    const projects = projectsData?.projects || [];
    const resourcesList = resourcesData?.resources?.resources || [];

    const totalProjects = projects.length;
    const activeProjects = projects.filter(p => p.status === 'Active' || p.status === 'In Progress' || p.status === 'Review');
    const totalActive = activeProjects.length;

    // Status breakdown
    const statusCounts = {
      Backlog: projects.filter(p => p.status === 'Backlog' || p.status === 'Discovery').length,
      Planned: projects.filter(p => p.status === 'Planned' || p.status === 'Planning').length,
      Active: activeProjects.length,
      Completed: projects.filter(p => p.status === 'Completed').length,
      'On Hold': projects.filter(p => p.status === 'On Hold').length,
    };

    // Avg active project completion progress rate
    const avgActiveProgress = totalActive > 0
      ? Math.round(activeProjects.reduce((sum, p) => sum + (p.progress || 0), 0) / totalActive)
      : 0;

    // Size breakdown
    const sizeCounts: Record<string, number> = {
      'XS': 0, 'S': 0, 'M': 0, 'L': 0, 'XL': 0
    };
    projects.forEach(p => {
      if (p.size && sizeCounts[p.size] !== undefined) {
        sizeCounts[p.size]++;
      }
    });

    // Deadlines calculations (upcoming 14 days)
    const now = new Date();
    const upcomingDeadlines = projects
      .filter(p => (p.status === 'Active' || p.status === 'In Progress' || p.status === 'Review') && p.dueDate)
      .map(p => {
        const due = new Date(p.dueDate!);
        const diffTime = due.getTime() - now.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return {
          id: p.id,
          name: p.name,
          client: p.client?.company || 'Sin Cliente',
          dueDate: p.dueDate!,
          progress: p.progress || 0,
          daysRemaining: diffDays
        };
      })
      .filter(item => item.daysRemaining >= 0)
      .sort((a, b) => a.daysRemaining - b.daysRemaining);

    const criticalCount = upcomingDeadlines.filter(item => item.daysRemaining <= 14).length;

    // Resource load calculations
    const totalResources = resourcesList.length;
    let totalLoad = 0;
    let overloadedCount = 0;

    const resourcesWorkload = resourcesList.map((res: any) => {
      const load = res.load !== undefined ? parseFloat(String(res.load)) : 0;
      totalLoad += load;
      if (load > 100) {
        overloadedCount++;
      }

      let category = 'balanced';
      if (load > 100) category = 'overloaded';
      else if (load < 70) category = 'underloaded';

      return {
        id: res.id,
        name: res.name,
        level: res.level || 'Mid',
        projectCount: res.projectCount || 0,
        load,
        category
      };
    }).sort((a: any, b: any) => b.load - a.load);

    const avgLoadRate = totalResources > 0 ? Math.round(totalLoad / totalResources) : 0;

    return {
      totalProjects,
      totalActive,
      statusCounts,
      avgActiveProgress,
      sizeCounts,
      upcomingDeadlines: upcomingDeadlines.slice(0, 5),
      criticalCount,
      totalResources,
      resourcesWorkload: resourcesWorkload.slice(0, 6),
      avgLoadRate,
      overloadedCount
    };
  }, [projectsData, resourcesData]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  // Sizing color palette
  const sizeColors: Record<string, string> = {
    'XS': 'var(--size-xs)',
    'S': 'var(--size-s)',
    'M': 'var(--size-m)',
    'L': 'var(--size-l)',
    'XL': 'var(--size-xl)'
  };

  // SVG Donut Chart Calculation variables
  const donutRadius = 40;
  const donutCirc = 2 * Math.PI * donutRadius; // ~251.3
  
  const donutSegments = [
    { label: 'Activos', count: stats.statusCounts.Active, color: 'var(--status-active)' },
    { label: 'Planificados', count: stats.statusCounts.Planned, color: 'var(--status-planned)' },
    { label: 'Completados', count: stats.statusCounts.Completed, color: 'var(--status-completed)' },
    { label: 'En Espera', count: stats.statusCounts['On Hold'], color: 'var(--status-onhold)' },
    { label: 'Backlog', count: stats.statusCounts.Backlog, color: 'var(--status-backlog)' }
  ].filter(s => s.count > 0);

  const totalSegmentProjects = donutSegments.reduce((sum, s) => sum + s.count, 0) || 1;
  let accumulatedPercent = 0;

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('es-ES', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <Activity size={24} style={{ color: 'var(--primary)' }} />
            Dashboard Directivo
          </h1>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>
            Resumen en tiempo real del estado de los proyectos, distribución del esfuerzo y capacidad de recursos.
          </p>
        </div>
      </div>

      <div className="page-content">
        <div className="dashboard-container">
          
          {/* Fila superior: Tarjetas de KPI */}
          <div className="kpi-grid">
            <div className="kpi-card" onClick={() => navigate('/projects')}>
              <div className="kpi-icon-wrapper" style={{ background: 'var(--primary-glow)', color: 'var(--primary)' }}>
                <Briefcase size={22} />
              </div>
              <div className="kpi-details">
                <span className="kpi-value">{stats.totalActive}</span>
                <span className="kpi-label">Proyectos Activos</span>
                <span className="kpi-trend up">
                  <TrendingUp size={12} /> {stats.totalProjects} totales
                </span>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrapper" style={{ background: 'var(--success-light)', color: 'var(--success-text)' }}>
                <CheckCircle size={22} />
              </div>
              <div className="kpi-details">
                <span className="kpi-value">{stats.avgActiveProgress}%</span>
                <span className="kpi-label">Progreso de Activos</span>
                <span className="kpi-trend up" style={{ color: 'var(--text-muted)' }}>
                  Promedio ponderado
                </span>
              </div>
            </div>

            <div className="kpi-card" onClick={() => navigate('/resources')}>
              <div className="kpi-icon-wrapper" style={{ 
                background: stats.overloadedCount > 0 ? 'var(--danger-light)' : 'var(--primary-glow)', 
                color: stats.overloadedCount > 0 ? 'var(--danger-text)' : 'var(--primary)' 
              }}>
                <Users size={22} />
              </div>
              <div className="kpi-details">
                <span className="kpi-value">{stats.avgLoadRate}%</span>
                <span className="kpi-label">Carga de Recursos</span>
                {stats.overloadedCount > 0 ? (
                  <span className="kpi-trend down">
                    <AlertTriangle size={12} /> {stats.overloadedCount} sobrecargados
                  </span>
                ) : (
                  <span className="kpi-trend up" style={{ color: 'var(--success-text)' }}>
                    Ocupación saludable
                  </span>
                )}
              </div>
            </div>

            <div className="kpi-card" onClick={() => navigate('/roadmap')}>
              <div className="kpi-icon-wrapper" style={{ 
                background: stats.criticalCount > 0 ? 'var(--warning-light)' : 'var(--primary-glow)', 
                color: stats.criticalCount > 0 ? 'var(--warning-text)' : 'var(--primary)' 
              }}>
                <Clock size={22} />
              </div>
              <div className="kpi-details">
                <span className="kpi-value">{stats.criticalCount}</span>
                <span className="kpi-label">Vencimientos &lt; 14 días</span>
                {stats.criticalCount > 0 ? (
                  <span className="kpi-trend down" style={{ color: 'var(--warning-text)' }}>
                    Requiere atención
                  </span>
                ) : (
                  <span className="kpi-trend up" style={{ color: 'var(--success-text)' }}>
                    Hitos bajo control
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Fila intermedia: Gráficos de Estado y Tallas */}
          <div className="dashboard-row">
            
            {/* Gráfico Donut de Proyectos */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Distribución por Estado</h3>
              </div>
              <div className="chart-container-donut">
                <div className="donut-svg-wrapper">
                  <svg viewBox="0 0 100 100" width="100%" height="100%">
                    {donutSegments.map((seg, idx) => {
                      const share = seg.count / totalSegmentProjects;
                      const strokeLength = share * donutCirc;
                      const strokeOffset = -(accumulatedPercent * donutCirc);
                      accumulatedPercent += share;

                      return (
                        <circle
                          key={idx}
                          cx="50"
                          cy="50"
                          r={donutRadius}
                          fill="transparent"
                          stroke={seg.color}
                          strokeWidth="10"
                          strokeDasharray={`${strokeLength} ${donutCirc}`}
                          strokeDashoffset={strokeOffset}
                          transform="rotate(-90 50 50)"
                          style={{ transition: 'all 0.3s ease' }}
                        />
                      );
                    })}
                  </svg>
                  <div className="donut-center-text">
                    <span className="donut-center-num">{stats.totalProjects}</span>
                    <span className="donut-center-lbl">Proyectos</span>
                  </div>
                </div>

                <div className="chart-legend">
                  {donutSegments.map((seg, idx) => (
                    <div key={idx} className="legend-item">
                      <div className="legend-label-group">
                        <span className="legend-dot" style={{ background: seg.color }} />
                        <span>{seg.label}</span>
                      </div>
                      <span className="legend-value">{seg.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Gráfico de Barras de Tallas */}
            <div className="dashboard-card">
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Esfuerzo por Talla de Proyecto</h3>
              </div>
              <div className="bar-chart-list">
                {['XS', 'S', 'M', 'L', 'XL'].map(size => {
                  const count = stats.sizeCounts[size] || 0;
                  const maxCount = Math.max(...Object.values(stats.sizeCounts), 1);
                  const pct = (count / maxCount) * 100;
                  return (
                    <div key={size} className="bar-chart-item">
                      <div className="bar-chart-label-row">
                        <span className="bar-chart-name">Talla {size}</span>
                        <span className="bar-chart-count">{count} {count === 1 ? 'proyecto' : 'proyectos'}</span>
                      </div>
                      <div className="bar-track">
                        <div
                          className="bar-fill"
                          style={{
                            width: `${pct}%`,
                            background: sizeColors[size] || 'var(--primary)'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

          {/* Fila inferior: Heatmap de Carga de Recursos y Deadlines */}
          <div className="dashboard-row">
            
            {/* Heatmap de Recursos */}
            <div className="dashboard-card" style={{ flex: 1 }}>
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Carga de Trabajo del Equipo</h3>
                <Link to="/resources" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '2px' }}>
                  Ver todos <ChevronRight size={14} />
                </Link>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table className="table" style={{ width: '100%', minWidth: '450px' }}>
                  <thead>
                    <tr>
                      <th>Desarrollador</th>
                      <th>Asignados</th>
                      <th>Ocupación / Carga</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.resourcesWorkload.map((res: any) => (
                      <tr key={res.id}>
                        <td>
                          <div className="workload-avatar-cell">
                            <div
                              className="avatar avatar-sm"
                              style={{
                                background: 'var(--primary-glow)',
                                color: 'var(--primary)',
                                fontWeight: 700,
                                width: '28px',
                                height: '28px',
                                fontSize: '11px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}
                            >
                              {res.name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2)}
                            </div>
                            <div>
                              <strong style={{ fontSize: '14px', color: 'var(--text-primary)' }}>{res.name}</strong>
                              <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{res.level}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', fontWeight: 600, color: 'var(--text-primary)' }}>
                          {res.projectCount}
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <div className="progress-bar" style={{ width: '80px', height: '8px' }}>
                              <div
                                className="progress-fill"
                                style={{
                                  width: `${Math.min(res.load, 100)}%`,
                                  background: res.category === 'overloaded' 
                                    ? 'var(--danger)' 
                                    : res.category === 'balanced'
                                      ? 'var(--success)'
                                      : 'var(--primary)'
                                }}
                              />
                            </div>
                            <span className={`workload-badge workload-${res.category}`}>
                              {res.load}% {res.category === 'overloaded' ? 'Sobrecarga' : res.category === 'balanced' ? 'Óptimo' : 'Libre'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Proyectos Próximos a Vencer (Línea de Tiempo de Riesgos) */}
            <div className="dashboard-card" style={{ flex: 1 }}>
              <div className="dashboard-card-header">
                <h3 className="dashboard-card-title">Vencimientos Críticos y Riesgos</h3>
                <Link to="/roadmap" style={{ fontSize: '13px', fontWeight: 600, color: 'var(--primary)', display: 'flex', alignItems: 'center', gap: '2px' }}>
                  Roadmap <ChevronRight size={14} />
                </Link>
              </div>

              <div className="critical-deadlines-list">
                {stats.upcomingDeadlines.map((proj: any) => (
                  <div key={proj.id} className="critical-deadline-item" onClick={() => navigate(`/projects/${proj.id}`)} style={{ cursor: 'pointer' }}>
                    <div className="critical-project-info">
                      <span className="critical-project-name">{proj.name}</span>
                      <span className="critical-project-client">Cliente: {proj.client}</span>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span className={`critical-deadline-date ${proj.daysRemaining > 14 ? 'warning' : ''}`}>
                        <Clock size={12} />
                        {proj.daysRemaining === 0 
                          ? 'Vence Hoy' 
                          : proj.daysRemaining === 1 
                            ? 'Vence Mañana' 
                            : `${proj.daysRemaining} días`}
                      </span>

                      {/* Mini círculo de progreso */}
                      <div className="critical-progress-circle">
                        <svg width="36" height="36" viewBox="0 0 36 36">
                          <circle cx="18" cy="18" r="15" fill="transparent" stroke="var(--border)" strokeWidth="3" />
                          <circle
                            cx="18"
                            cy="18"
                            r="15"
                            fill="transparent"
                            stroke="var(--primary)"
                            strokeWidth="3"
                            strokeDasharray={`${(proj.progress / 100) * 94.2} 94.2`}
                            transform="rotate(-90 18 18)"
                          />
                        </svg>
                        <span className="critical-pct-text">{proj.progress}%</span>
                      </div>
                    </div>
                  </div>
                ))}

                {stats.upcomingDeadlines.length === 0 && (
                  <div className="phase-empty-state" style={{ padding: '40px 0' }}>
                    <CheckCircle size={32} style={{ color: 'var(--success-text)', marginBottom: '8px' }} />
                    <p>No hay vencimientos de proyectos programados a corto plazo.</p>
                  </div>
                )}
              </div>
            </div>

          </div>

        </div>
      </div>
    </>
  );
}

export default Dashboard;
